"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { cn } from "@/lib/utils";
import UserProfileDropdown from "@/components/UserProfileDropdown";

const NAV_LINKS = [
    { href: "/groups", label: "Groups" },
    { href: "/volunteers", label: "Volunteers" },
];

export default function TopNav() {
    const { user, isLoading } = useWorkspace();
    const pathname = usePathname();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close mobile menu on outside click + on route change
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, []);
    useEffect(() => { setMenuOpen(false); }, [pathname]);

    return (
        <nav className="top-nav px-4 sm:px-6 py-3 sm:py-4 bg-white/90 backdrop-blur-md border-b-2 border-[#003d45]/10 sticky top-0 z-50">
            <div className="max-w-[1536px] mx-auto flex items-center justify-between gap-2">
                <Link href="/" className="flex items-center gap-2 sm:gap-3 no-underline group min-w-0">
                    <img src="/favicon.ico" alt="Logo" className="w-8 h-8 rounded-lg shadow-lg group-hover:scale-110 transition-transform flex-shrink-0" />
                    <span className="text-base sm:text-xl font-black text-[#003d45] tracking-tighter truncate">
                        VOLUNTEER HUB
                    </span>
                </Link>

                <div className="flex items-center gap-1">
                    {/* Inline links — desktop only */}
                    <div className="hidden md:flex items-center gap-1">
                        {NAV_LINKS.map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(
                                    "px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all",
                                    pathname === link.href ? "text-white" : "text-[#468892] hover:bg-[#003d45]/5"
                                )}
                                style={pathname === link.href ? { backgroundColor: "#003d45" } : {}}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </div>

                    {/* Auth section — always visible so dashboard access never disappears */}
                    {isLoading ? (
                        <div className="w-10 h-10 rounded-full border-2 border-slate-100 animate-pulse ml-2 md:ml-4" />
                    ) : user ? (
                        <div className="ml-2 md:ml-4">
                            <UserProfileDropdown />
                        </div>
                    ) : (
                        <Link href="/login" className="hidden md:inline-block ml-4 px-6 py-2.5 bg-[#AE232D] text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#C1121F] transition-all shadow-xl shadow-[#AE232D]/20 no-underline">
                            Login Member
                        </Link>
                    )}

                    {/* Mobile hamburger */}
                    <div className="relative md:hidden" ref={menuRef}>
                        <button
                            onClick={() => setMenuOpen(o => !o)}
                            className="p-2 rounded-lg hover:bg-[#003d45]/5 transition-colors text-[#003d45]"
                            aria-label="Open menu"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d={menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 7h16M4 12h16M4 17h16"} />
                            </svg>
                        </button>

                        {menuOpen && (
                            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl border border-[#003d45]/10 shadow-2xl shadow-[#003d45]/20 overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="p-2">
                                    {user && (
                                        <Link href="/dashboard" className="flex items-center gap-2 px-4 py-3 rounded-xl hover:bg-[#003d45]/5 text-[#003d45] text-xs font-black uppercase tracking-widest no-underline">
                                            My Dashboard
                                        </Link>
                                    )}
                                    {NAV_LINKS.map(link => (
                                        <Link
                                            key={link.href}
                                            href={link.href}
                                            className={cn(
                                                "flex items-center gap-2 px-4 py-3 rounded-xl text-xs font-black uppercase tracking-widest no-underline transition-colors",
                                                pathname === link.href ? "bg-[#003d45] text-white" : "text-[#468892] hover:bg-[#003d45]/5"
                                            )}
                                        >
                                            {link.label}
                                        </Link>
                                    ))}
                                    {!user && (
                                        <Link href="/login" className="flex items-center gap-2 px-4 py-3 mt-1 rounded-xl bg-[#AE232D] text-white text-xs font-black uppercase tracking-widest no-underline justify-center">
                                            Login Member
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}
